import { useState, type ReactNode } from 'react';
import { DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent, type UniqueIdentifier } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { pointerOrKeyboardCollision } from './pointerOrKeyboardCollision';
import { useSortableLayersAnnouncements } from './useSortableLayersAnnouncements';
import { DragCursor } from './DragCursor';

export interface SortableLayersItem {
  id: UniqueIdentifier;
  name: string;
}

export interface SortableLayersProps {
  items: SortableLayersItem[];
  /** Fired once with the pre-drop indices; the caller owns the actual array move + persistence. */
  onReorder: (fromIndex: number, toIndex: number) => void;
  /**
   * Optional guard (additive, LLAMS-610 DB-12): the reason a move `fromIndex -> toIndex` is refused,
   * or `null` when allowed. A refused drop never reaches `onReorder`, and the reason is announced
   * once - in place of the "moved to" / "dropped at" message - so the caller needs no second live region.
   */
  rejectMove?: (fromIndex: number, toIndex: number) => string | null;
  /** Optional: how a landing slot reads aloud (default `position <n>`), e.g. `Bill to, position <n>`. */
  describePosition?: (id: UniqueIdentifier, position: number) => string;
  /** Fired when a pick-up starts (pointer or keyboard). */
  onDragStart?: () => void;
  /** Fired when a drag ends without a reorder callback (cancelled, or dropped where it started). */
  onDragCancel?: () => void;
  children: ReactNode;
}

/**
 * A reusable, keyboard-operable dnd-kit sortable wrapper for Layers rows — `PointerSensor` +
 * `KeyboardSensor` (`sortableKeyboardCoordinates`: Space to pick up, arrows to move, Space to
 * drop, Escape to cancel — built into dnd-kit's `KeyboardSensor`, nothing bespoke needed here),
 * with named pickup/over/drop/cancel announcements (`useSortableLayersAnnouncements`).
 */
export function SortableLayers({
  items,
  onReorder,
  rejectMove,
  describePosition,
  onDragStart,
  onDragCancel,
  children
}: SortableLayersProps) {
  const [dragging, setDragging] = useState(false);
  const ids = items.map((item) => item.id);

  const getName = (id: UniqueIdentifier) => items.find((item) => item.id === id)?.name ?? String(id);
  const getPosition = (id: UniqueIdentifier) => {
    const index = ids.findIndex((x) => x === id);
    return index >= 0 ? index + 1 : null;
  };
  const getRejection = (activeId: UniqueIdentifier, overId: UniqueIdentifier) => {
    if (!rejectMove) return null;
    const fromIndex = ids.findIndex((id) => id === activeId);
    const toIndex = ids.findIndex((id) => id === overId);
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return null;
    return rejectMove(fromIndex, toIndex);
  };
  const announcements = useSortableLayersAnnouncements({ getName, getPosition, describePosition, getRejection });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    setDragging(false);
    const overId = event.over?.id;
    if (overId == null || event.active.id === overId) {
      onDragCancel?.();
      return;
    }

    const fromIndex = ids.findIndex((id) => id === event.active.id);
    const toIndex = ids.findIndex((id) => id === overId);
    if (fromIndex < 0 || toIndex < 0) return;
    if (rejectMove?.(fromIndex, toIndex)) {
      onDragCancel?.();
      return;
    }

    onReorder(fromIndex, toIndex);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerOrKeyboardCollision}
      onDragStart={() => {
        setDragging(true);
        onDragStart?.();
      }}
      onDragEnd={handleDragEnd}
      onDragCancel={() => {
        setDragging(false);
        onDragCancel?.();
      }}
      accessibility={{
        announcements,
        screenReaderInstructions: {
          draggable:
            'To pick up a row, press space or enter. While dragging, use the arrow keys to move the row. Press space or enter again to drop. Press escape to cancel.'
        }
      }}
    >
      <DragCursor active={dragging} />
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}
